import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import Layout from "../components/Layout";
import { CATEGORIES } from "./VehiculesListe";
import { formaterEuros, formaterDate, periodeInversee, MESSAGE_PERIODE_INVERSEE } from "../format";
import { labelTypeRecurrence, texteJoursRestants, classeUrgence } from "../recurrences";
import { useModules } from "../modules";
import { SqueletteIndicateurs, SqueletteTableau } from "../components/Squelette";
import ParcVide from "../components/ParcVide";

function Carte({ label, valeur, onClick, accent, retard }) {
  return (
    <div
      className={
        "carte-indicateur" +
        (accent ? " carte-accent" : "") +
        (retard ? " carte-retard" : "") +
        (onClick ? " cliquable" : "")
      }
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => (e.key === "Enter" ? onClick() : null) : undefined}
    >
      <span className="indicateur-valeur">{valeur}</span>
      <span className="indicateur-label">{label}</span>
    </div>
  );
}

function PaginationTravaux({ page, nbPages, total, onPage }) {
  if (nbPages <= 1) return null;
  return (
    <div className="pagination">
      <button className="btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        ← Précédent
      </button>
      <span className="pagination-info">
        Page {page}/{nbPages} · {total} échéance{total > 1 ? "s" : ""}
      </span>
      <button className="btn" disabled={page >= nbPages} onClick={() => onPage(page + 1)}>
        Suivant →
      </button>
    </div>
  );
}

// Date minimale pour un report : aujourd'hui (pas dans le passé).
const MIN_DATE_REPORT = new Date().toISOString().slice(0, 10);
// Date par défaut proposée : demain.
const dateDemain = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();

  // Véhicules : on ne garde que les COMPTEURS de la réponse, jamais ses
  // lignes (voir le chargement plus bas). L'ancien commentaire promettait ici
  // que « 500 couvre largement toute flotte réaliste » — c'était vrai pour
  // l'affichage et faux pour le calcul, qui se faisait sur ces 500 lignes.
  const [vehiculesData, setVehiculesData] = useState(null);

  const [synthese, setSynthese] = useState(null);
  const modules = useModules();
  const [erreur, setErreur] = useState("");
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");

  // Travaux à venir : paginés
  const [travauxPage, setTravauxPage] = useState(null);
  const [pageTravaux, setPageTravaux] = useState(1);
  // « À COMPLÉTER » EST UNE LISTE À PART, et c'est le point de la correction
  // du 31/08/2026. Ces échéances portent une date DÉDUITE de la mise en
  // circulation, jamais confirmée : les afficher dans un tableau intitulé
  // « Travaux à venir » revenait à annoncer des travaux datés de 2015 sur un
  // véhicule créé le matin même. C'est le premier écran que voit un nouveau
  // client après sa première action réussie.
  //
  // Deux requêtes plutôt qu'un tri côté écran : chaque bloc pagine sur son
  // propre contenu, et les compteurs du serveur restent exacts.
  const [aCompleterPage, setACompleterPage] = useState(null);
  const [pageACompleter, setPageACompleter] = useState(1);
  const [inspectionsEnCours, setInspectionsEnCours] = useState({});
  const [demarrage, setDemarrage] = useState(null);
  // Le catalogue dit quels types ont une FICHE a remplir ici. Sans lui,
  // l'ecran proposait « Demarrer » sur une assurance : le serveur ne connait
  // que le CT et la VGP en inspection, le bouton echouait a chaque clic.
  const [typesEcheance, setTypesEcheance] = useState([]);

  // État de la modale de report
  const [modalReport, setModalReport] = useState(null);
  const [reportDate, setReportDate] = useState("");
  const [reportMotif, setReportMotif] = useState("");
  const [reportEnvoi, setReportEnvoi] = useState(false);

  // --- Chargement des COMPTEURS véhicules (une seule fois) ---
  // `page_size: 1` n'est pas une coquille : cet écran n'affiche AUCUNE ligne
  // de véhicule, seulement des nombres. Or tous ces nombres sont comptés en
  // base et voyagent hors de la page (`total`, `total_sortis`, `nb_hayon`,
  // `counts_by_categorie`) : demander les lignes reviendrait à télécharger
  // toute la flotte pour n'en afficher aucune.
  //
  // Il demandait 500 lignes et comptait dessus. Le total venait du serveur,
  // donc juste ; les catégories et les hayons étaient comptés sur les lignes
  // reçues. Au-delà de 500 véhicules, le même écran affichait « 600 véhicules
  // en parc » au-dessus de compteurs qui n'en totalisaient que 500 — sans que
  // rien ne le signale.
  useEffect(() => {
    api
      .get("/vehicules", { params: { page_size: 1 } })
      .then(({ data }) => setVehiculesData(data))
      .catch(() => setErreur("Impossible de charger les véhicules."));
  }, []);

  // --- Chargement travaux paginés ---
  const chargerTravaux = useCallback(() => {
    api
      .get("/travaux-a-venir",
           { params: { page: pageTravaux, page_size: 20, a_renseigner: false } })
      .then(({ data }) => setTravauxPage(data))
      .catch(() => setErreur("Impossible de charger les travaux à venir."));
  }, [pageTravaux]);

  const chargerACompleter = useCallback(() => {
    api
      .get("/travaux-a-venir",
           { params: { page: pageACompleter, page_size: 20, a_renseigner: true } })
      .then(({ data }) => setACompleterPage(data))
      .catch(() => setErreur("Impossible de charger les dates à renseigner."));
  }, [pageACompleter]);

  useEffect(() => {
    chargerTravaux();
  }, [chargerTravaux]);

  useEffect(() => {
    chargerACompleter();
  }, [chargerACompleter]);

  useEffect(() => {
    api
      .get("/echeances/types")
      .then(({ data }) => setTypesEcheance(data))
      .catch(() => {
        /* Catalogue indisponible : on retombe sur le comportement prudent
           ci-dessous, qui ne propose « Demarrer » sur rien. */
      });
  }, []);

  /** Ce type a-t-il une fiche d'inspection a remplir dans l'application ? */
  const aUneFiche = (type) =>
    typesEcheance.find((t) => t.valeur === type)?.inspection === true;

  // --- Inspections en cours : rechargées à chaque changement de page travaux ---
  const travaux = travauxPage?.items ?? [];
  useEffect(() => {
    if (travaux.length === 0) return;
    const vids = [...new Set(travaux.map((t) => t.vehicule_id))];
    Promise.all(
      vids.map((vid) =>
        api
          .get(`/vehicules/${vid}/inspections`)
          .then(({ data }) => ({ vid, data }))
          .catch(() => ({ vid, data: [] }))
      )
    ).then((resultats) => {
      const lookup = {};
      resultats.forEach(({ vid, data }) => {
        data
          .filter((insp) => insp.statut === "en_cours")
          .forEach((insp) => {
            lookup[`${vid}_${insp.type_inspection}`] = insp.id;
          });
      });
      setInspectionsEnCours(lookup);
    });
  }, [travauxPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const chargerSynthese = useCallback(() => {
    if (periodeInversee(dateDebut, dateFin)) return;
    const params = {};
    if (dateDebut) params.date_debut = dateDebut;
    if (dateFin) params.date_fin = dateFin;
    api
      .get("/couts/synthese", { params })
      .then(({ data }) => setSynthese(data))
      .catch(() => setErreur("Impossible de charger la synthèse des coûts."));
  }, [dateDebut, dateFin]);

  useEffect(() => {
    chargerSynthese();
  }, [chargerSynthese]);

  // --- Dérivés véhicules ---
  // `total` ne compte QUE le parc : le serveur écarte les véhicules sortis
  // (vendus, accidentés) par défaut. C'est ce que promet le libellé.
  // TOUS ces compteurs viennent du SERVEUR, aucun n'est calculé ici : ils
  // portent sur la flotte entière, pas sur les lignes reçues.
  const total = vehiculesData?.total ?? 0;
  const nbSortis = vehiculesData?.total_sortis ?? 0;
  const nbHayon = vehiculesData?.nb_hayon ?? 0;
  const parCategorie = (valeur) => vehiculesData?.counts_by_categorie?.[valeur] ?? 0;
  const coutTotal = synthese?.cout_total_flotte_ht ?? 0;
  // Le diviseur compte les véhicules SORTIS eux aussi, parce que le numérateur
  // les compte : la synthèse porte tout l'historique, et l'entretien d'un
  // camion vendu en mars a bien été payé. Diviser une dépense de 108 camions
  // par les 98 restants gonflerait la moyenne sans que rien ne le signale.
  const nbVehiculesCoutes = total + nbSortis;
  const coutMoyen = nbVehiculesCoutes > 0 ? Number(coutTotal) / nbVehiculesCoutes : 0;

  // --- Totaux travaux (depuis la réponse, pas depuis la page courante) ---
  const nbRetard = travauxPage?.nb_retard ?? 0;
  const nbAVenir = travauxPage?.nb_a_venir ?? 0;
  // Échéances dont la date est déduite d'un import, jamais confirmée. Elles
  // ne sont PAS du retard, et leur carte n'est pas rouge : c'est une saisie
  // qui reste à faire, pas un camion en infraction.
  //
  // Le compte vient de LEUR requête, pas de celle des travaux : chaque appel
  // ne compte que ce qu'il a demandé. Lire `nb_a_renseigner` sur la réponse
  // filtrée « dates connues » afficherait toujours zéro — un compteur muet,
  // et le bloc à compléter disparaîtrait de l'écran sans que rien ne l'explique.
  const nbARenseigner = aCompleterPage?.total ?? 0;

  const demarrerOuReprendre = async (e, t) => {
    e.stopPropagation();
    const cle = `${t.vehicule_id}_${t.type_recurrence}`;
    const prefixe = t.type_recurrence === "vgp_hayon" ? "vgp" : "ct";
    const inspExistante = inspectionsEnCours[cle];

    if (inspExistante) {
      navigate(`/vehicules/${t.vehicule_id}/${prefixe}/${inspExistante}`);
      return;
    }

    setDemarrage(t.id);
    try {
      const { data } = await api.post(`/vehicules/${t.vehicule_id}/inspections`, {
        type_inspection: t.type_recurrence,
        recurrence_id: t.id,
      });
      navigate(`/vehicules/${t.vehicule_id}/${prefixe}/${data.id}`);
    } catch {
      setErreur("Impossible de démarrer l'inspection.");
      setDemarrage(null);
    }
  };

  const marquerEffectuee = async (e, t) => {
    e.stopPropagation();
    setDemarrage(t.id);
    try {
      await api.post(`/recurrences/${t.id}/effectuer`);
      // La ligne disparait de la liste : le serveur historise l'echeance
      // courante et cree la suivante, plus loin dans le temps.
      chargerTravaux();
    } catch {
      setErreur("Impossible d'enregistrer cette échéance comme effectuée.");
    } finally {
      setDemarrage(null);
    }
  };

  const ouvrirModalReport = (e, t) => {
    e.stopPropagation();
    setModalReport(t);
    setReportDate(dateDemain());
    setReportMotif("");
  };

  const soumettreReport = async () => {
    if (!reportDate) return;
    setReportEnvoi(true);
    try {
      await api.post(`/recurrences/${modalReport.id}/reporter`, {
        nouvelle_date: reportDate,
        motif: reportMotif.trim() || null,
      });
      chargerTravaux();
      setModalReport(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErreur(typeof detail === "string" ? detail : "Impossible de reporter l'échéance.");
    } finally {
      setReportEnvoi(false);
    }
  };

  const isAdmin = user.role === "admin";

  return (
    <Layout user={user} onLogout={onLogout}>
      <h2>Tableau de bord</h2>
      {erreur && <div className="erreur">{erreur}</div>}

      {/* Le premier écran d'un client neuf : des cartes à « 0 » ne disent pas
          quoi faire. On attend la réponse du serveur — afficher « parc vide »
          avant elle mentirait à une flotte de 108 camions. */}
      {vehiculesData !== null && total === 0 && nbSortis === 0 && (
        <ParcVide user={user} />
      )}

      {/* Chaque rangée attend SA source. Un squelette global tant que la plus
          lente n'a pas répondu masquerait des chiffres déjà prêts. */}
      {vehiculesData === null ? (
        <SqueletteIndicateurs nombre={2 + CATEGORIES.length} />
      ) : (
      <div className="grille-indicateurs">
        <Carte label="Véhicules en parc" valeur={total} accent onClick={() => navigate("/vehicules")} />
        <Carte
          label="Équipés d'un hayon"
          valeur={nbHayon}
          onClick={() => navigate("/vehicules")}
        />
        {/* Affichée seulement s'il y en a : une carte « 0 sorti du parc »
            occuperait une place sans rien apprendre. */}
        {nbSortis > 0 && (
          <Carte
            label="Sortis du parc"
            valeur={nbSortis}
            onClick={() => navigate("/vehicules?etat=sortis")}
          />
        )}
        {CATEGORIES.map((c) => (
          <Carte
            key={c.valeur}
            label={c.label}
            valeur={parCategorie(c.valeur)}
            onClick={() => navigate(`/vehicules?categorie=${c.valeur}`)}
          />
        ))}
      </div>
      )}

      {travauxPage === null ? (
        <SqueletteIndicateurs nombre={nbARenseigner > 0 ? 3 : 2} />
      ) : (
      <div className="grille-indicateurs grille-reglementaire">
        <Carte label="Travaux en retard" valeur={nbRetard} retard={nbRetard > 0} />
        <Carte label="Travaux à venir" valeur={nbAVenir} accent />
        {/* Carte affichée SEULEMENT s'il y en a : un « 0 date à renseigner »
            permanent n'apprend rien et encombre le tableau de bord d'un client
            dont le parc est saisi correctement. */}
        {nbARenseigner > 0 && (
          <Carte label="Dates à renseigner" valeur={nbARenseigner} />
        )}
      </div>
      )}

      {/* Filtre période (coûts) */}
      <div className="filtres-dates">
        <label>
          Du
          <input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} />
        </label>
        <label>
          Au
          <input type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} />
        </label>
        {(dateDebut || dateFin) && (
          <button
            className="btn"
            onClick={() => {
              setDateDebut("");
              setDateFin("");
            }}
          >
            Réinitialiser
          </button>
        )}
      </div>

      {/* Rangée 3 : coûts */}
      {/* Bloc masque si le module Couts & Carburant n'est pas souscrit :
          le serveur refuse la synthese (403), et une rangee de « 0 € » serait
          pire qu'une absence -- elle se lit comme une flotte sans depenses. */}
      {!modules.module_operations ? null : periodeInversee(dateDebut, dateFin) ? (
        <p className="erreur-champ">{MESSAGE_PERIODE_INVERSEE}</p>
      ) : synthese === null ? (
        <SqueletteIndicateurs nombre={4} />
      ) : (
      <div className="grille-indicateurs grille-couts">
        <Carte label="Coût total de la flotte" valeur={formaterEuros(coutTotal)} accent />
        <Carte label="Coût moyen / véhicule" valeur={formaterEuros(coutMoyen)} />
        {/* Le total annoncait « cout de la flotte » en ne comptant que
            l'entretien : le gazole pese 30 a 40 % d'une flotte de transport,
            un responsable de parc voyait l'incoherence en trois secondes. */}
        <Carte
          label="dont carburant"
          valeur={formaterEuros(synthese?.cout_carburant_ht ?? 0)}
        />
        <Carte
          label="dont entretien"
          valeur={formaterEuros(synthese?.cout_entretien_ht ?? 0)}
        />
      </div>
      )}

      {/* Travaux à venir : liste détaillée CT / VGP avec actions et pagination */}
      <section className="carte">
        <h3>Travaux à venir</h3>
        {/* « Aucune échéance » s'affichait pendant le chargement, sur une
            flotte qui en compte 112 : un état vide affiché avant les données
            n'est pas une attente, c'est une information fausse. */}
        {travauxPage !== null && travaux.length === 0 ? (
          <p className="vide">Aucune échéance réglementaire à suivre.</p>
        ) : (
          <>
            <table className="tableau">
              <thead>
                <tr>
                  <th>Véhicule</th>
                  <th>Échéance</th>
                  <th>Date</th>
                  <th>Statut</th>
                  <th></th>
                </tr>
              </thead>
              {travauxPage === null ? (
                <SqueletteTableau lignes={6} colonnes={5} />
              ) : (
              <tbody>
                {travaux.map((t) => {
                  const cle = `${t.vehicule_id}_${t.type_recurrence}`;
                  const inspExistante = inspectionsEnCours[cle];
                  const enCours = demarrage === t.id;
                  return (
                    <tr
                      key={t.id}
                      className={"ligne-cliquable echeance-ligne " + classeUrgence(t)}
                      onClick={() => navigate(`/vehicules/${t.vehicule_id}`)}
                    >
                      <td>
                        <strong>{t.immatriculation}</strong>
                      </td>
                      <td>{labelTypeRecurrence(t.type_recurrence)}</td>
                      <td>
                        {formaterDate(t.date_prochaine_echeance)}
                        {t.date_dernier_report && (
                          <>
                            <br />
                            <span
                              className="sous-info"
                              title={t.motif_dernier_report || ""}
                            >
                              ↪ Reportée
                              {t.motif_dernier_report ? ` — ${t.motif_dernier_report}` : ""}
                            </span>
                          </>
                        )}
                      </td>
                      <td className="jours">{texteJoursRestants(t.jours_restants, t)}</td>
                      <td className="actions-cellule">
                        {aUneFiche(t.type_recurrence) ? (
                          <button
                            className={inspExistante ? "btn btn-travaux" : "btn-accent btn-travaux"}
                            disabled={enCours}
                            onClick={(e) => demarrerOuReprendre(e, t)}
                          >
                            {enCours ? "…" : inspExistante ? "Reprendre" : "Démarrer"}
                          </button>
                        ) : (
                          /* Assurance, tachygraphe : rien a remplir ici, l'acte
                             a lieu chez un tiers. On enregistre qu'il est fait. */
                          <button
                            className="btn-accent btn-travaux"
                            disabled={enCours}
                            onClick={(e) => marquerEffectuee(e, t)}
                          >
                            {enCours ? "…" : "Marquer effectuée"}
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            className="btn btn-travaux"
                            onClick={(e) => ouvrirModalReport(e, t)}
                          >
                            Reporter
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              )}
            </table>

            {travauxPage && (
              <PaginationTravaux
                page={travauxPage.page}
                nbPages={travauxPage.nb_pages}
                total={travauxPage.total}
                onPage={setPageTravaux}
              />
            )}
          </>
        )}
      </section>

      {/* À COMPLÉTER — le bloc qui sort les dates déduites des « travaux ».
          Il n'apparaît que s'il y en a : un client dont le parc est saisi
          correctement n'a pas à voir une section vide.

          LA DATE N'EST PAS AFFICHÉE, et c'est délibéré. Elle existe en base —
          la colonne ne peut pas être vide, et le tri doit rester sensé — mais
          la montrer reviendrait à annoncer « VGP hayon · 12/07/2015 » sur un
          camion enregistré ce matin. Ce n'est pas une échéance, c'est une
          ignorance : on demande la date, on ne l'invente pas. */}
      {nbARenseigner > 0 && (
        <section className="carte">
          <h3>À compléter</h3>
          <p className="note-info">
            Ces dates ont été calculées à partir de la mise en circulation, faute
            de mieux — elles ne sont pas connues. Ouvrez la fiche du véhicule et
            saisissez la date du dernier contrôle : la prochaine échéance
            deviendra juste, et le véhicule sortira de cette liste.
          </p>
          <table className="tableau">
            <thead>
              <tr>
                <th>Véhicule</th>
                <th>Échéance</th>
                <th>Dernier contrôle</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(aCompleterPage?.items ?? []).map((t) => (
                <tr
                  key={t.id}
                  className="ligne-cliquable"
                  onClick={() => navigate(`/vehicules/${t.vehicule_id}`)}
                >
                  <td><strong>{t.immatriculation}</strong></td>
                  <td>{labelTypeRecurrence(t.type_recurrence)}</td>
                  <td className="sous-info">inconnu</td>
                  <td className="actions-cellule">
                    <button
                      className="btn btn-travaux"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/vehicules/${t.vehicule_id}`);
                      }}
                    >
                      Renseigner
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {aCompleterPage && (
            <PaginationTravaux
              page={aCompleterPage.page}
              nbPages={aCompleterPage.nb_pages}
              total={aCompleterPage.total}
              onPage={setPageACompleter}
            />
          )}
        </section>
      )}

      {/* Modale de report d'échéance */}
      {modalReport && (
        <div className="modal-overlay" onClick={() => setModalReport(null)}>
          <div className="modal-carte" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-titre">Reporter une échéance</h3>
            <p className="modal-sous-titre">
              {labelTypeRecurrence(modalReport.type_recurrence)} — {modalReport.immatriculation}
            </p>
            <label className="modal-champ">
              Nouvelle date *
              <input
                type="date"
                min={MIN_DATE_REPORT}
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
              />
            </label>
            <label className="modal-champ">
              Motif (optionnel)
              <input
                type="text"
                placeholder="Ex. Client indisponible, Panne à traiter avant…"
                value={reportMotif}
                onChange={(e) => setReportMotif(e.target.value)}
              />
            </label>
            <div className="modal-actions">
              <button className="btn" onClick={() => setModalReport(null)}>
                Annuler
              </button>
              <button
                className="btn-accent"
                onClick={soumettreReport}
                disabled={!reportDate || reportEnvoi}
              >
                {reportEnvoi ? "…" : "Confirmer le report"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
